import { useState } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { ArrowDown, ArrowUp, Check, Plus, Trash2 } from 'lucide-react'
import { api } from '../../lib/api.js'
import { toast } from '../../components/feedback/Toast.jsx'

/**
 * Category management: rename, re-icon, reorder, delete, create.
 * Deleting is only possible once a category is empty — the server refuses
 * otherwise, because pages cascade with their space.
 */
export default function SpaceManager() {
  const qc = useQueryClient()
  const { data } = useQuery({ queryKey: ['spaces'], queryFn: () => api('/spaces') })
  const spaces = data?.spaces || []
  const [adding, setAdding] = useState(false)

  const refresh = () => qc.invalidateQueries({ queryKey: ['spaces'] })

  async function move(space, dir) {
    const sorted = [...spaces].sort((a, b) => a.position - b.position)
    const i = sorted.findIndex((s) => s.slug === space.slug)
    const j = i + dir
    if (j < 0 || j >= sorted.length) return
    const other = sorted[j]
    try {
      await api(`/spaces/${space.slug}`, { method: 'PATCH', body: { position: other.position } })
      await api(`/spaces/${other.slug}`, { method: 'PATCH', body: { position: space.position } })
      refresh()
    } catch (err) {
      toast(err.message || 'Couldn’t reorder', 'error')
    }
  }

  async function remove(space) {
    if (space.pageCount > 0) return
    if (!window.confirm(`Delete the empty category “${space.name}”?`)) return
    try {
      await api(`/spaces/${space.slug}`, { method: 'DELETE' })
      refresh()
      toast('Category deleted')
    } catch (err) {
      toast(err.data?.error || err.message, 'error')
    }
  }

  return (
    <section>
      <h2 className="section-header mb-2">Categories</h2>
      <div className="card divide-y divide-hairline">
        {spaces.map((s) => (
          <SpaceRow key={s.slug} space={s} onMove={move} onRemove={remove} onSaved={refresh} />
        ))}
        {adding ? (
          <NewSpaceRow
            onDone={(created) => {
              setAdding(false)
              if (created) refresh()
            }}
          />
        ) : (
          <button onClick={() => setAdding(true)} className="tap flex w-full items-center gap-2 px-4 py-3 text-sm font-medium text-link active:bg-grouped">
            <Plus size={15} /> New category
          </button>
        )}
      </div>
      <p className="mt-2 text-xs text-faint">
        A category can be deleted once it's empty. New categories appear in the sidebar and under More.
      </p>
    </section>
  )
}

function SpaceRow({ space, onMove, onRemove, onSaved }) {
  const [icon, setIcon] = useState(space.icon || '')
  const [name, setName] = useState(space.name)
  const dirty = icon !== (space.icon || '') || name !== space.name
  const [busy, setBusy] = useState(false)

  async function save() {
    if (!name.trim()) return
    setBusy(true)
    try {
      await api(`/spaces/${space.slug}`, { method: 'PATCH', body: { name: name.trim(), icon: icon.trim() || null } })
      onSaved()
      toast('Saved')
    } catch (err) {
      toast(err.message || 'Couldn’t save', 'error')
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className="flex items-center gap-1.5 px-3 py-2">
      <input
        value={icon}
        onChange={(e) => setIcon(e.target.value)}
        aria-label="Icon"
        className="w-9 shrink-0 rounded-lg border border-transparent bg-transparent py-1.5 text-center text-sm focus:border-hairline focus:bg-white focus:outline-none"
      />
      <input
        value={name}
        onChange={(e) => setName(e.target.value)}
        aria-label="Name"
        className="min-w-0 flex-1 rounded-lg border border-transparent bg-transparent px-2 py-1.5 text-sm font-medium text-ink focus:border-hairline focus:bg-white focus:outline-none"
      />
      <span className="shrink-0 pr-1 text-xs text-faint">{space.pageCount}</span>
      {dirty ? (
        <button onClick={save} disabled={busy} title="Save" className="tap flex items-center justify-center rounded-lg text-link active:bg-grouped">
          <Check size={16} />
        </button>
      ) : (
        <>
          <button onClick={() => onMove(space, -1)} title="Move up" className="tap flex items-center justify-center rounded-lg text-faint active:bg-grouped">
            <ArrowUp size={14} />
          </button>
          <button onClick={() => onMove(space, 1)} title="Move down" className="tap flex items-center justify-center rounded-lg text-faint active:bg-grouped">
            <ArrowDown size={14} />
          </button>
          <button
            onClick={() => onRemove(space)}
            disabled={space.pageCount > 0}
            title={space.pageCount > 0 ? 'Empty the category first' : 'Delete category'}
            className="tap flex items-center justify-center rounded-lg text-faint active:text-danger disabled:opacity-30"
          >
            <Trash2 size={14} />
          </button>
        </>
      )}
    </div>
  )
}

function NewSpaceRow({ onDone }) {
  const [icon, setIcon] = useState('')
  const [name, setName] = useState('')
  const [busy, setBusy] = useState(false)

  async function create() {
    if (!name.trim()) return onDone(false)
    setBusy(true)
    try {
      await api('/spaces', { body: { name: name.trim(), icon: icon.trim() || null } })
      toast('Category created')
      onDone(true)
    } catch (err) {
      toast(err.data?.error || err.message, 'error')
      setBusy(false)
    }
  }

  return (
    <div className="flex items-center gap-1.5 px-3 py-2">
      <input value={icon} onChange={(e) => setIcon(e.target.value)} placeholder="🎛" className="w-9 shrink-0 rounded-lg border border-hairline bg-white py-1.5 text-center text-[16px] focus:outline-none" />
      <input
        autoFocus
        value={name}
        onChange={(e) => setName(e.target.value)}
        onKeyDown={(e) => e.key === 'Enter' && create()}
        placeholder="Category name"
        className="min-w-0 flex-1 rounded-lg border border-hairline bg-white px-2 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-link"
      />
      <button onClick={create} disabled={busy || !name.trim()} className="tap rounded-lg px-2 text-sm font-medium text-link disabled:opacity-40">
        Add
      </button>
    </div>
  )
}
